import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import LiveActivity from "../components/LiveActivity";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth, useTheme } from "../App";
import { 
  Volume2, 
  TrendingUp, 
  TrendingDown,
  Minus,
  Sparkles,
  Flame,
  Trophy,
  Target,
  BookOpen,
  Headphones,
  MessageCircle,
  Users,
  Eye,
  Play,
  ChevronRight,
  ArrowUp,
  Star,
  Zap,
  Calendar,
  Hash,
  Moon,
  Sun,
  ArrowLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Trends() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [trendingWords, setTrendingWords] = useState([]);
  const [expression, setExpression] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [popularContent, setPopularContent] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);

  // Handle hashtag click - navigate to relevant page
  const handleHashtagClick = (tag) => {
    // Map hashtags to actions
    const hashtagActions = {
      '#MotDuJour': () => navigate('/dictionary'),
      '#ApprensLeBaoulé': () => navigate(user ? '/learn' : '/register'),
      '#ProverbesSages': () => navigate('/dictionary?category=proverbes'),
      '#CultureIvoirienne': () => navigate('/community'),
      '#LangueMaternelle': () => navigate('/dictionary'),
      '#PatrimoineAfricain': () => navigate('/community'),
      '#BaouléChallenge': () => navigate(user ? '/quiz' : '/register'),
      '#TraditionOrale': () => navigate('/dictionary?category=expressions'),
    };

    const action = hashtagActions[tag];
    if (action) {
      action();
    } else {
      // Default: go to dictionary
      navigate('/dictionary');
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [wordsRes, exprRes, challRes, popRes, hashRes] = await Promise.all([
          fetch(`${API}/trends/words`),
          fetch(`${API}/trends/expression`),
          fetch(`${API}/trends/challenges`),
          fetch(`${API}/trends/popular-content`),
          fetch(`${API}/trends/hashtags`)
        ]);

        if (wordsRes.ok) setTrendingWords(await wordsRes.json());
        if (exprRes.ok) setExpression(await exprRes.json());
        if (challRes.ok) setChallenges(await challRes.json());
        if (popRes.ok) setPopularContent(await popRes.json());
        if (hashRes.ok) setHashtags(await hashRes.json());
      } catch (error) {
        console.error("Error fetching trends:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const playAudio = async (wordId) => {
    if (playingId) return;
    setPlayingId(wordId);
    try {
      const audio = new Audio(`${API}/words/${wordId}/audio`);
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      await audio.play();
    } catch { setPlayingId(null); }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up": return <TrendingUp className="w-4 h-4 text-green-500" />;
      case "down": return <TrendingDown className="w-4 h-4 text-red-500" />;
      case "new": return <Sparkles className="w-4 h-4 text-primary" />;
      default: return <Minus className="w-4 h-4 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 animate-pulse mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des tendances...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="trends-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b-4 border-primary shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-2">
              <div className="text-secondary font-heading font-black text-2xl">BAOULÉ MÉDIA</div>
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted">
              {theme === 'dark' ? <Sun className="w-5 h-5 text-primary" /> : <Moon className="w-5 h-5" />}
            </button>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="rounded-full">Dashboard</Button>
            ) : (
              <Button onClick={() => navigate('/register')} className="rounded-full">Commencer</Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-bold mb-4">
            <Flame className="w-5 h-5" />
            TENDANCES
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black mb-4">
            Ce qui est <span className="text-primary">populaire</span> maintenant
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Découvrez les mots les plus appris, les défis du jour et l'expression de la semaine
          </p>
        </div>

        {/* Trending Hashtags Bar */}
        <div className="mb-12 p-4 rounded-2xl bg-accent/20 border-2 border-accent animate-slide-up">
          <div className="flex items-center gap-6 overflow-x-auto">
            <div className="flex items-center gap-2 bg-accent text-foreground px-4 py-2 rounded-xl font-bold whitespace-nowrap">
              <Hash className="w-4 h-4" />
              HASHTAGS
            </div>
            {hashtags.map((h, i) => (
              <button 
                key={i} 
                onClick={() => handleHashtagClick(h.tag)}
                className="flex items-center gap-2 whitespace-nowrap cursor-pointer hover:text-primary hover:bg-primary/10 px-3 py-1.5 rounded-full transition-all"
                data-testid={`hashtag-${h.tag.replace('#', '')}`}
              >
                <span className="font-bold">{h.tag}</span>
                <span className="text-xs text-muted-foreground">{h.posts}</span>
                {getTrendIcon(h.trend)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Expression of the Week */}
            {expression && (
              <Card className="overflow-hidden border-2 border-secondary animate-slide-up">
                <div className="bg-secondary text-white p-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-bold uppercase tracking-wide">Expression de la Semaine</span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h2 className="font-heading text-3xl sm:text-4xl font-black text-primary mb-2">
                    "{expression.baoulé}"
                  </h2>
                  <p className="text-xl font-semibold mb-4">{expression.français}</p>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong className="text-foreground">💡 Signification:</strong> {expression.signification}</p>
                    <p><strong className="text-foreground">🎯 Contexte:</strong> {expression.contexte}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trending Words */}
            <Card className="animate-slide-up stagger-1">
              <CardHeader className="border-b-4 border-foreground">
                <CardTitle className="font-heading text-2xl flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-primary" />
                  Mots Tendance
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {trendingWords.map((word, index) => (
                    <div 
                      key={word.word_id} 
                      className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate('/dictionary')}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-500 text-white' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading text-xl font-bold">{word.baoulé}</h3>
                          {word.trend === "new" && (
                            <span className="px-2 py-0.5 rounded bg-primary text-white text-xs font-bold">NEW</span>
                          )}
                        </div>
                        <p className="text-muted-foreground">{word.français}</p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm">
                            <Eye className="w-4 h-4" />
                            <span className="font-bold">{word.views?.toLocaleString()}</span>
                          </div>
                          {word.trend === "up" && word.trend_percent > 0 && (
                            <div className="flex items-center gap-1 text-green-500 text-xs">
                              <ArrowUp className="w-3 h-3" />
                              <span>+{word.trend_percent}%</span>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={(e) => { e.stopPropagation(); playAudio(word.word_id); }}
                          className={`p-3 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors ${
                            playingId === word.word_id ? 'audio-playing' : ''
                          }`}
                        >
                          <Volume2 className="w-5 h-5 text-primary" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Daily Challenges */}
            {challenges && (
              <Card className="animate-slide-up stagger-2">
                <CardHeader className="border-b-4 border-foreground">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <Target className="w-6 h-6 text-accent" />
                      Défis du Jour
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">
                      {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {challenges.challenges?.map((challenge) => (
                      <div 
                        key={challenge.id}
                        className="p-4 rounded-xl border-2 border-border hover:border-primary/50 transition-colors cursor-pointer group"
                        onClick={() => navigate(user ? '/dashboard' : '/register')}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-4xl">{challenge.icon}</div>
                          <div className="flex-1">
                            <h4 className="font-bold mb-1 group-hover:text-primary transition-colors">
                              {challenge.title}
                            </h4>
                            <p className="text-sm text-muted-foreground mb-3">{challenge.description}</p>
                            <div className="flex items-center justify-between">
                              <Progress value={0} className="flex-1 h-2 mr-3" />
                              <div className="flex items-center gap-1 text-primary font-bold text-sm">
                                <Star className="w-4 h-4" />
                                +{challenge.reward_points}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {!user && (
                    <div className="mt-6 p-4 rounded-xl bg-primary/10 text-center">
                      <p className="text-sm text-muted-foreground mb-3">
                        Connectez-vous pour participer aux défis et gagner des points !
                      </p>
                      <Button onClick={() => navigate('/register')} className="rounded-full">
                        Créer un compte
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Activity Stats - DYNAMIQUE avec LiveActivity */}
            <LiveActivity />

            {/* Trending Categories */}
            {popularContent && (
              <Card className="animate-slide-up stagger-2">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Flame className="w-5 h-5 text-primary" />
                    Catégories Populaires
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {popularContent.trending_categories?.map((cat, i) => (
                      <div key={cat.name} className="flex items-center gap-3">
                        <span className="w-6 text-center font-bold text-muted-foreground">{i + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{cat.name}</span>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-muted-foreground">{cat.count}</span>
                              {getTrendIcon(cat.trend)}
                            </div>
                          </div>
                          <Progress value={(cat.count / 500) * 100} className="h-1.5" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Contributors */}
            {popularContent && (
              <Card className="animate-slide-up stagger-3">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Top Contributeurs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {popularContent.top_contributors?.map((user, i) => (
                      <div key={user.user_id} className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                          i === 0 ? 'bg-yellow-500 text-white' :
                          i === 1 ? 'bg-gray-400 text-white' :
                          i === 2 ? 'bg-orange-500 text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>{i + 1}</span>
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10"></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm">{user.name}</p>
                        </div>
                        <span className="font-bold text-primary">{user.points}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full mt-4 rounded-full"
                    onClick={() => navigate('/community')}
                  >
                    Voir le classement complet
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="bg-gradient-to-br from-primary to-destructive text-white animate-slide-up stagger-4">
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-xl font-bold mb-2">Participez !</h3>
                <p className="text-white/80 text-sm mb-4">
                  Apprenez, contribuez et grimpez dans le classement
                </p>
                <Button 
                  className="bg-white text-primary hover:bg-white/90 rounded-full w-full"
                  onClick={() => navigate(user ? '/dashboard' : '/register')}
                >
                  {user ? 'Aller au Dashboard' : 'Créer un compte'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
